import { BALANCE } from '../config/balance';
import { DEFAULT_DIFFICULTY, isDifficultyTier, type DifficultyTier } from '../config/difficulty';
import {
  validateLevel,
  type LevelDefinition,
  type LevelPortalDefinition,
  type LevelZoneDefinition,
} from '../world/LevelDefinition';
import type { SurfaceId } from '../world/Surface';
import { CELL_SIZE, componentSpec, footprint, type ComponentKind } from './components';

/**
 * The builder's document: a 2D layout of components plus the things placed
 * inside them in 3D.
 *
 * Everything is stored in the document's own terms — cells for the layout,
 * metres for what is inside — and `compileMap` turns it into the `LevelDefinition`
 * JSON the game already runs. Nothing in the runtime knows the builder exists.
 */

export type AssetKind = 'crate' | 'console' | 'pillar' | 'barrier';
export type Rotation = 0 | 90 | 180 | 270;

export interface MapComponent {
  id: string;
  kind: ComponentKind;
  /** Grid cell of the footprint's minimum corner. */
  cell: [number, number];
  /** Footprint in cells, stored so a saved map does not depend on the specs. */
  cells: [number, number];
  rotation: Rotation;
}

/** A box placed inside a room in the 3D builder. Metres, world space. */
export interface MapAsset {
  kind: AssetKind;
  position: [number, number, number];
  size: [number, number, number];
}

/** A drone anchor placed in the 3D builder. Metres, world space. */
export interface MapDrone {
  position: [number, number, number];
  surface: SurfaceId;
  magnetic: boolean;
  /**
   * A looping patrol route, in world metres, authored in the 3D builder.
   *
   * The points are where the drone hovers from — the anchor track on the
   * surface — so the runtime can add its own hover offset. Two or more points
   * make a loop; fewer are ignored, which lets a route be drawn one waypoint at
   * a time.
   */
  route?: Array<[number, number, number]>;
}

/** How close a waypoint has to be to a drone to join its route, metres. */
export const WAYPOINT_ATTACH_RANGE = 12;

export interface MapDocument {
  version: 1;
  name: string;
  seed: number;
  difficulty: DifficultyTier;
  components: MapComponent[];
  assets: MapAsset[];
  drones: MapDrone[];
}

/** A component's footprint in world metres. */
export interface ComponentRect {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  centreX: number;
  centreZ: number;
  width: number;
  depth: number;
  height: number;
}

export function createMap(name = 'Untitled Deck'): MapDocument {
  return {
    version: 1,
    name,
    seed: 1337,
    difficulty: DEFAULT_DIFFICULTY,
    components: [],
    assets: [],
    drones: [],
  };
}

export function rectOf(component: MapComponent): ComponentRect {
  const [width, depth] = footprint(component.kind, component.rotation, component.cells);
  const minX = component.cell[0] * CELL_SIZE;
  const minZ = component.cell[1] * CELL_SIZE;
  return {
    minX,
    maxX: minX + width * CELL_SIZE,
    minZ,
    maxZ: minZ + depth * CELL_SIZE,
    centreX: minX + (width * CELL_SIZE) / 2,
    centreZ: minZ + (depth * CELL_SIZE) / 2,
    width: width * CELL_SIZE,
    depth: depth * CELL_SIZE,
    height: componentSpec(component.kind).height,
  };
}

/** A fresh component id: stable, readable, and unique inside the map. */
export function nextComponentId(map: MapDocument): string {
  let index = map.components.length + 1;
  const taken = new Set(map.components.map((component) => component.id));
  while (taken.has(`zone-${index}`)) index += 1;
  return `zone-${index}`;
}

/** True when two footprints share any floor area. */
export function rectanglesOverlap(a: ComponentRect, b: ComponentRect): boolean {
  return a.minX < b.maxX && b.minX < a.maxX && a.minZ < b.maxZ && b.minZ < a.maxZ;
}

/**
 * Every footprint that overlaps an existing one, as pairs of ids.
 *
 * A map with an overlap cannot compile: two zones occupying the same space
 * would put two floors in one place. The editor refuses those placements, and
 * this is the check that keeps a loaded draft honest.
 */
export function overlappingIds(map: MapDocument): Array<[string, string]> {
  const pairs: Array<[string, string]> = [];
  for (let i = 0; i < map.components.length; i += 1) {
    for (let j = i + 1; j < map.components.length; j += 1) {
      const a = map.components[i]!;
      const b = map.components[j]!;
      if (rectanglesOverlap(rectOf(a), rectOf(b))) pairs.push([a.id, b.id]);
    }
  }
  return pairs;
}

/** The component whose footprint contains a world position, if any. */
export function componentAt(map: MapDocument, x: number, z: number): MapComponent | undefined {
  return map.components.find((component) => {
    const rect = rectOf(component);
    return x >= rect.minX && x <= rect.maxX && z >= rect.minZ && z <= rect.maxZ;
  });
}

/** Where a run on this map starts: the first component's floor centre. */
export function spawnPoint(map: MapDocument): [number, number, number] {
  const first = map.components[0];
  if (!first) return [0, 0, 0];
  const rect = rectOf(first);
  return [rect.centreX, 0, rect.centreZ];
}

/**
 * True when an asset would stand in the body at a spawn point.
 *
 * The spawn is a point on the floor, and a crate sits *above* the floor, so a
 * plain point-in-box test would never fire. The body's own volume is what
 * matters: its footprint plus its radius, and the height it stands at.
 */
function spawnBlockedBy(spawn: [number, number, number], asset: MapAsset): boolean {
  const radius = BALANCE.player.bodyRadius;
  if (Math.abs(spawn[0] - asset.position[0]) > asset.size[0] / 2 + radius) return false;
  if (Math.abs(spawn[2] - asset.position[2]) > asset.size[2] / 2 + radius) return false;
  const bottom = asset.position[1] - asset.size[1] / 2;
  const top = asset.position[1] + asset.size[1] / 2;
  const bodyTop = BALANCE.player.attachHeight + radius;
  return bottom < bodyTop && top > 0.02;
}

/** Problems that stop a map compiling, in player-readable terms. */
export function mapProblems(map: MapDocument): string[] {
  const problems: string[] = [];
  if (map.components.length === 0) problems.push('The map has no components yet.');
  for (const [a, b] of overlappingIds(map)) {
    problems.push(`${a} overlaps ${b}.`);
  }
  if (map.components.length > 0) {
    const spawn = spawnPoint(map);
    for (const asset of map.assets) {
      if (spawnBlockedBy(spawn, asset)) {
        problems.push('The spawn point is buried in an asset.');
        break;
      }
    }
  }
  for (const drone of map.drones) {
    if (!componentAt(map, drone.position[0], drone.position[2])) {
      problems.push('A drone sits outside every component.');
      break;
    }
  }
  for (const asset of map.assets) {
    if (!componentAt(map, asset.position[0], asset.position[2])) {
      problems.push('An asset sits outside every component.');
      break;
    }
  }
  return problems;
}

/** How much of a doorway's width must stay clear for a body to pass, metres. */
export const DOORWAY_CLEARANCE = 1.2;
/** How close to a doorway's plane a box has to be to count as in the way. */
const DOORWAY_REACH = 0.7;

/**
 * Things that will play badly, which the builder shows but does not block on.
 *
 * A blocked doorway is the one worth calling out: a doorway is generated from
 * where two components touch, so a pillar placed near that shared wall lands in
 * the opening and the deck reads as broken. The editor reports it and lets the
 * player decide, because a deliberately walled-off door is a design choice and
 * refusing to test the map over it would be worse.
 */
export function mapWarnings(map: MapDocument): string[] {
  const warnings: string[] = [];
  const routeLeaves = map.drones.some((drone) =>
    (drone.route ?? []).some(([x, , z]) => !componentAt(map, x, z)),
  );
  if (routeLeaves) warnings.push('A drone route leaves the deck.');
  for (const portal of compilePortals(map)) {
    const alongIsX = portal.axis === 'x';
    const [px, , pz] = portal.position;
    const plane = alongIsX ? pz : px;
    for (const asset of map.assets) {
      const [ax, ay, az] = asset.position;
      const [sx, sy, sz] = asset.size;
      const along = alongIsX ? ax : az;
      const alongHalf = (alongIsX ? sx : sz) / 2;
      const depth = alongIsX ? az : ax;
      const depthHalf = (alongIsX ? sz : sx) / 2;
      const centre = alongIsX ? px : pz;

      // In the opening at all?
      if (Math.abs(depth - plane) - depthHalf > DOORWAY_REACH) continue;
      if (ay - sy / 2 > portal.height) continue;
      // And covering the middle of it, where the body has to pass.
      if (Math.abs(along - centre) - alongHalf >= DOORWAY_CLEARANCE / 2) continue;
      warnings.push(
        `A doorway between ${portal.a} and ${portal.b} is blocked by a piece of cover.`,
      );
      break;
    }
  }
  return warnings;
}

/**
 * Compile a map for the *game*: null when the map has problems or the result
 * does not validate.
 *
 * This is the guarded path a boot uses, so a stale or hand-edited map in
 * session storage can never leave the player staring at a boot error instead of
 * a level.
 */
export function compileMapForGame(map: MapDocument): LevelDefinition | null {
  if (mapProblems(map).length > 0) return null;
  const level = compileMap(map);
  return validateLevel(level).ok ? level : null;
}

export function addComponent(
  map: MapDocument,
  kind: ComponentKind,
  cell: [number, number],
  rotation: Rotation = 0,
): MapComponent {
  const component: MapComponent = {
    id: nextComponentId(map),
    kind,
    cell,
    cells: footprint(kind, rotation),
    rotation,
  };
  map.components.push(component);
  return component;
}

/** Remove a component and everything placed inside it. */
export function removeComponent(map: MapDocument, id: string): void {
  const component = map.components.find((entry) => entry.id === id);
  if (!component) return;
  const rect = rectOf(component);
  map.components = map.components.filter((entry) => entry.id !== id);
  const inside = (x: number, z: number): boolean =>
    x >= rect.minX && x <= rect.maxX && z >= rect.minZ && z <= rect.maxZ;
  map.assets = map.assets.filter((asset) => !inside(asset.position[0], asset.position[2]));
  map.drones = map.drones.filter((drone) => !inside(drone.position[0], drone.position[2]));
}

/**
 * Move a component to a new cell, carrying whatever was placed inside it.
 *
 * Without this, dragging a room in the 2D editor would leave its crates and
 * drones floating in space.
 */
export function moveComponent(map: MapDocument, id: string, cell: [number, number]): void {
  const component = map.components.find((entry) => entry.id === id);
  if (!component) return;
  const before = rectOf(component);
  const dx = (cell[0] - component.cell[0]) * CELL_SIZE;
  const dz = (cell[1] - component.cell[1]) * CELL_SIZE;
  if (dx === 0 && dz === 0) return;
  component.cell = [cell[0], cell[1]];
  const wasInside = (x: number, z: number): boolean =>
    x >= before.minX && x <= before.maxX && z >= before.minZ && z <= before.maxZ;
  for (const asset of map.assets) {
    if (wasInside(asset.position[0], asset.position[2])) {
      asset.position = [asset.position[0] + dx, asset.position[1], asset.position[2] + dz];
    }
  }
  for (const drone of map.drones) {
    if (wasInside(drone.position[0], drone.position[2])) {
      drone.position = [drone.position[0] + dx, drone.position[1], drone.position[2] + dz];
      // A route belongs to its drone, so it travels with the anchor instead of
      // being left behind in the room it was drawn in.
      if (drone.route) {
        drone.route = drone.route.map(
          ([x, y, z]) => [x + dx, y, z + dz] as [number, number, number],
        );
      }
    }
  }
}

/**
 * The face of a component nearest to a point, used to keep a drone anchored to
 * the wall it was placed against when its room is turned.
 */
export function nearestSurface(rect: ComponentRect, position: [number, number, number]): SurfaceId {
  const distances: Array<[SurfaceId, number]> = [
    ['floor', Math.abs(position[1])],
    ['ceiling', Math.abs(rect.height - position[1])],
    ['left', Math.abs(rect.minX - position[0])],
    ['right', Math.abs(rect.maxX - position[0])],
    ['front', Math.abs(rect.minZ - position[2])],
    ['back', Math.abs(rect.maxZ - position[2])],
  ];
  distances.sort((a, b) => a[1] - b[1]);
  return distances[0]![0];
}

/**
 * Turn a component a quarter turn, carrying whatever is inside it.
 *
 * The contents turn with the room (their offset from its centre rotates), and a
 * drone's anchor face is re-derived from where it ends up, so it stays on the
 * wall it was placed against rather than on a face it no longer touches.
 */
export function rotateComponent(map: MapDocument, id: string): void {
  const component = map.components.find((entry) => entry.id === id);
  if (!component) return;
  const before = rectOf(component);

  component.rotation = ((component.rotation + 90) % 360) as Rotation;
  component.cells = footprint(component.kind, component.rotation);
  const after = rectOf(component);

  const turn = (x: number, z: number): [number, number] => {
    if (x < before.minX || x > before.maxX || z < before.minZ || z > before.maxZ) return [x, z];
    // Quarter turn about the room's centre, then clamp into the new footprint
    // (a rotated rectangle is not the same rectangle, even when its area is).
    const localX = x - before.centreX;
    const localZ = z - before.centreZ;
    const rotatedX = after.centreX - localZ;
    const rotatedZ = after.centreZ + localX;
    return [
      Math.min(Math.max(rotatedX, after.minX + 1), after.maxX - 1),
      Math.min(Math.max(rotatedZ, after.minZ + 1), after.maxZ - 1),
    ];
  };

  for (const asset of map.assets) {
    const [x, z] = turn(asset.position[0], asset.position[2]);
    asset.position = [x, asset.position[1], z];
  }
  for (const drone of map.drones) {
    const [x, z] = turn(drone.position[0], drone.position[2]);
    const moved = x !== drone.position[0] || z !== drone.position[2];
    drone.position = [x, drone.position[1], z];
    if (moved) drone.surface = nearestSurface(after, drone.position);
    if (drone.route) {
      drone.route = drone.route.map(([px, py, pz]) => {
        const [rx, rz] = turn(px, pz);
        return [rx, py, rz] as [number, number, number];
      });
    }
  }
}

export function addAsset(map: MapDocument, asset: MapAsset): void {
  map.assets.push(asset);
}

export function removeAssetAt(map: MapDocument, x: number, y: number, z: number): boolean {
  const index = map.assets.findIndex((asset) => {
    const [ax, ay, az] = asset.position;
    const [sx, sy, sz] = asset.size;
    return (
      Math.abs(x - ax) <= sx / 2 + 0.01 &&
      Math.abs(y - ay) <= sy / 2 + 0.01 &&
      Math.abs(z - az) <= sz / 2 + 0.01
    );
  });
  if (index < 0) return false;
  map.assets.splice(index, 1);
  return true;
}

export function removeDroneAt(map: MapDocument, x: number, y: number, z: number): boolean {
  const index = map.drones.findIndex((drone) => {
    const [dx, dy, dz] = drone.position;
    return Math.hypot(x - dx, y - dy, z - dz) < 1.2;
  });
  if (index < 0) return false;
  map.drones.splice(index, 1);
  return true;
}

/**
 * The drone a waypoint would join: the nearest anchor within reach.
 *
 * A route is authored by placing waypoints, so the drone they belong to has to
 * be inferred from where the player is aiming. The nearest drone is the one the
 * player is drawing a route around.
 */
export function nearestDroneIndex(
  map: MapDocument,
  x: number,
  y: number,
  z: number,
  maxDistance = WAYPOINT_ATTACH_RANGE,
): number | null {
  let best: number | null = null;
  let bestDistance = maxDistance;
  map.drones.forEach((drone, index) => {
    const [dx, dy, dz] = drone.position;
    const distance = Math.hypot(x - dx, y - dy, z - dz);
    if (distance <= bestDistance) {
      bestDistance = distance;
      best = index;
    }
  });
  return best;
}

/** Add a point to a drone's route, unless it is already there. */
export function appendWaypoint(drone: MapDrone, point: [number, number, number]): boolean {
  const route = drone.route ?? [];
  const duplicate = route.some(
    ([x, y, z]) => Math.hypot(x - point[0], y - point[1], z - point[2]) < 0.5,
  );
  if (duplicate) return false;
  route.push([point[0], point[1], point[2]]);
  drone.route = route;
  return true;
}

/** Remove the route point nearest a position, across every drone. */
export function removeWaypointAt(
  map: MapDocument,
  x: number,
  y: number,
  z: number,
  tolerance = 1.4,
): boolean {
  let bestDrone: MapDrone | undefined;
  let bestIndex = -1;
  let bestDistance = tolerance;
  for (const drone of map.drones) {
    const route = drone.route;
    if (!route) continue;
    for (let index = 0; index < route.length; index += 1) {
      const point = route[index]!;
      const distance = Math.hypot(x - point[0], y - point[1], z - point[2]);
      if (distance <= bestDistance) {
        bestDistance = distance;
        bestDrone = drone;
        bestIndex = index;
      }
    }
  }
  if (!bestDrone || bestIndex < 0) return false;
  const remaining = bestDrone.route?.filter((_, index) => index !== bestIndex);
  if (remaining && remaining.length > 0) bestDrone.route = remaining;
  else delete bestDrone.route;
  return true;
}

/** The centroid of every component, used to frame the map. */
export function mapBounds(map: MapDocument): {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
} {
  if (map.components.length === 0) return { minX: 0, maxX: 0, minZ: 0, maxZ: 0 };
  const rects = map.components.map(rectOf);
  return {
    minX: Math.min(...rects.map((rect) => rect.minX)),
    maxX: Math.max(...rects.map((rect) => rect.maxX)),
    minZ: Math.min(...rects.map((rect) => rect.minZ)),
    maxZ: Math.max(...rects.map((rect) => rect.maxZ)),
  };
}

const DEFAULT_SURFACES = {
  floor: { enabled: true, magnetic: true },
  ceiling: { enabled: true, magnetic: true },
  left: { enabled: true, magnetic: true },
  right: { enabled: true, magnetic: true },
  front: { enabled: true, magnetic: true },
  back: { enabled: true, magnetic: true },
};

/** The doorways between components that touch, with a margin off each corner. */
export function compilePortals(map: MapDocument): LevelPortalDefinition[] {
  const portals: LevelPortalDefinition[] = [];
  for (let i = 0; i < map.components.length; i += 1) {
    for (let j = i + 1; j < map.components.length; j += 1) {
      const a = map.components[i]!;
      const b = map.components[j]!;
      const ra = rectOf(a);
      const rb = rectOf(b);

      // Touching in z: the shared line runs along x, so the doorway does too.
      // A corner touch has no overlap along x and is filtered out below.
      const touchZ = ra.maxZ === rb.minZ || rb.maxZ === ra.minZ;
      if (touchZ) {
        const overlap = Math.min(ra.maxX, rb.maxX) - Math.max(ra.minX, rb.minX);
        if (overlap >= CELL_SIZE) {
          portals.push({
            id: `${a.id}-${b.id}`,
            a: a.id,
            b: b.id,
            axis: 'x',
            position: [
              (Math.max(ra.minX, rb.minX) + Math.min(ra.maxX, rb.maxX)) / 2,
              0,
              ra.maxZ === rb.minZ ? ra.maxZ : rb.maxZ,
            ],
            width: Math.min(overlap - 1, 3.2),
            height: Math.min(ra.height, rb.height, 3),
          });
        }
      }

      // Touching in x: the shared line runs along z.
      const touchX = ra.maxX === rb.minX || rb.maxX === ra.minX;
      if (touchX) {
        const overlap = Math.min(ra.maxZ, rb.maxZ) - Math.max(ra.minZ, rb.minZ);
        if (overlap >= CELL_SIZE) {
          portals.push({
            id: `${a.id}-${b.id}-x`,
            a: a.id,
            b: b.id,
            axis: 'z',
            position: [
              ra.maxX === rb.minX ? ra.maxX : rb.maxX,
              0,
              (Math.max(ra.minZ, rb.minZ) + Math.min(ra.maxZ, rb.maxZ)) / 2,
            ],
            width: Math.min(overlap - 1, 3.2),
            height: Math.min(ra.height, rb.height, 3),
          });
        }
      }
    }
  }
  return portals;
}

/**
 * Turn a map into the level the game runs.
 *
 * The zones are the components, the portals are the doorways between the ones
 * that touch, the assets and drones are the placements, and the spawn is the
 * first component's floor centre. The result is ordinary `LevelDefinition`
 * JSON: drop it in `src/levels/` and add it to the catalog and it is a level.
 */
export function compileMap(map: MapDocument): LevelDefinition {
  const rects = map.components.map(rectOf);
  const bounds = mapBounds(map);
  const height = rects.length > 0 ? Math.max(...rects.map((rect) => rect.height)) : 4;

  const zones: LevelZoneDefinition[] = map.components.map((component) => {
    const rect = rectOf(component);
    return {
      id: component.id,
      origin: [rect.centreX, 0, rect.centreZ],
      size: [rect.width, rect.height, rect.depth],
    };
  });

  const zoneOf = (x: number, z: number): string =>
    componentAt(map, x, z)?.id ?? map.components[0]?.id ?? map.name;

  const targets: LevelDefinition['targets'] = map.drones.map((drone) => ({
    type: 'drone',
    zone: zoneOf(drone.position[0], drone.position[2]),
    surface: drone.surface,
    position: drone.position,
    magnetic: drone.magnetic,
    ...(drone.route && drone.route.length >= 2
      ? { route: drone.route.map(([x, y, z]) => [x, y, z] as [number, number, number]) }
      : {}),
  }));

  const obstacles: LevelDefinition['obstacles'] = map.assets.map((asset) => ({
    type: 'box',
    kind: asset.kind,
    zone: zoneOf(asset.position[0], asset.position[2]),
    position: asset.position,
    size: asset.size,
  }));

  const spawn = spawnPoint(map);

  return {
    id: slugify(map.name),
    name: map.name,
    difficulty: map.difficulty,
    size: [bounds.maxX - bounds.minX, height, bounds.maxZ - bounds.minZ],
    surfaces: DEFAULT_SURFACES,
    zones,
    portals: compilePortals(map),
    spawn: {
      zone: map.components[0]?.id,
      surface: 'floor',
      position: spawn,
      facing: 0,
    },
    targets,
    obstacles,
    style: {
      wallColor: 0x2a3038,
      floorColor: 0x1b2026,
      ceilingColor: 0xc9d6e2,
      pathColor: 0xffb347,
      ceilingLights: rects.map((rect) => ({
        position: [rect.centreX, rect.height - 0.1, rect.centreZ] as [number, number, number],
        size: [2.4, 0.08, 1.2] as [number, number, number],
      })),
    },
    seed: map.seed,
  };
}

/** A level id from a display name: lowercase, hyphenated, ASCII. */
export function slugify(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug.length > 0 ? slug : 'custom-deck';
}

/** Read a draft back, filling in anything a saved file is missing. */
export function parseMap(input: unknown): MapDocument | null {
  if (typeof input !== 'object' || input === null) return null;
  const raw = input as Partial<MapDocument>;
  if (!Array.isArray(raw.components)) return null;
  const map = createMap(typeof raw.name === 'string' && raw.name ? raw.name : 'Untitled Deck');
  map.seed = Number.isFinite(raw.seed) ? Number(raw.seed) : map.seed;
  map.difficulty = isDifficultyTier(raw.difficulty) ? raw.difficulty : DEFAULT_DIFFICULTY;
  // A hand-edited file can carry anything: keep the entries that describe a
  // real component and drop the rest, so the editor never renders NaN.
  map.components = (raw.components as unknown[]).filter(isMapComponent);
  map.assets = (Array.isArray(raw.assets) ? raw.assets : []).filter(isMapAsset);
  map.drones = (Array.isArray(raw.drones) ? raw.drones : []).filter(isMapDrone);
  for (const drone of map.drones) {
    if (drone.route && drone.route.length === 0) delete drone.route;
  }
  return map;
}

const COMPONENT_KIND_SET = new Set<string>(['room', 'hall', 'junction', 'bay']);
const SURFACE_ID_SET = new Set<string>(['floor', 'ceiling', 'left', 'right', 'front', 'back']);

/** A fixed-length tuple of finite numbers, of whatever width is expected. */
function isNumberTuple(value: unknown, length: number): boolean {
  return (
    Array.isArray(value) &&
    value.length === length &&
    value.every((entry) => typeof entry === 'number' && Number.isFinite(entry))
  );
}

function isNumberTriple(value: unknown): value is [number, number, number] {
  return isNumberTuple(value, 3);
}

function isMapComponent(value: unknown): value is MapComponent {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Partial<MapComponent>;
  return (
    typeof entry.id === 'string' &&
    typeof entry.kind === 'string' &&
    COMPONENT_KIND_SET.has(entry.kind) &&
    // A component's cell and footprint are pairs, not triples.
    isNumberTuple(entry.cell, 2) &&
    isNumberTuple(entry.cells, 2) &&
    typeof entry.rotation === 'number'
  );
}

function isMapAsset(value: unknown): value is MapAsset {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Partial<MapAsset>;
  return (
    typeof entry.kind === 'string' && isNumberTriple(entry.position) && isNumberTriple(entry.size)
  );
}

function isMapDrone(value: unknown): value is MapDrone {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Partial<MapDrone>;
  return (
    isNumberTriple(entry.position) &&
    typeof entry.surface === 'string' &&
    SURFACE_ID_SET.has(entry.surface) &&
    (entry.route === undefined ||
      (Array.isArray(entry.route) && entry.route.every((point) => isNumberTriple(point))))
  );
}
